///
/// EarthBrowser
///
/// Created by Matt Giger on 11/27/2025
/// Copyright (c) 2025 Geodata Labs
///

import { EntityCameraAdapter } from "@/earthbrowser/datasets/EntityCameraAdapter"
import { type InfoPanelData, type InfoPanelProvider } from "@/earthbrowser/datasets/InfoPanelProvider"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { GLTF } from "@/earthbrowser/entities/GLTF/GLTF"
import { SelectionSphereNode } from "@/earthbrowser/entities/SelectionSphere/SelectionSphereNode"
import { geographic } from "@/earthbrowser/utils/geographic"
import { pushTargetToHistory } from "@/utils/navigationHistory"
import { classifyBuoy, isDartEventMode, isDartReporting } from "./buoyClassification"
import { BuoyMarkerNode, BuoyMarkerShape, type BuoyMarkerStyle } from "./BuoyMarkerNode"
import type { BuoyEntry } from "@shared-types/buoy"
import type { Feature, Point } from "@shared-types/geojson"
import { type Camera } from "@stovokor/camera/Camera"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { type RenderActionSet } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type FocusTransitionConfig } from "@stovokor/gestures/FocusTarget"
import { GestureNode } from "@stovokor/gestures/GestureNode"
import { type GestureHitResult } from "@stovokor/gestures/GestureRecognizer"
import { trig, vec3 } from "@stovokor/math"
import { animation, easing } from "@stovokor/math/animation"
import { type color, rgba } from "@stovokor/math/color"
import { matrix } from "@stovokor/math/matrix"
import { sphere } from "@stovokor/math/sphere"
import { type StovokorContext } from "@stovokor/StovokorContext"

const cBuoyModelURL = 'https://tiles.earthbrowser.com/static/models/buoy.glb'
const cBuoyModelAltitudeMeters = .03  // 30m above sea level (floating on water)
const cHitSphereRadius = 4            // km - minimum hit detection radius
const cModelScale = 0.2               // scale for 3D model

const cBuoyFocusDistance = 30
const cBuoyFocusMaxZoomRadius = 100

// Camera distance (km) under which the 3D model is rendered
const cModelVisibleDistance = 300

// Marker sizes in km at view scale 1 (what you see zoomed in). A sea-state
// buoy grows with wave height so big seas are big on the globe; the other
// classes are fixed and smaller so the moored buoys read first
const cSeaStateMinRadiusKm = 0.9
const cSeaStateRadiusPerMeterKm = 0.35
const cSeaStateMaxWaveMeters = 7
const cCoastalRadiusKm = 0.8
const cSilentRadiusKm = 0.7
const cDartRadiusKm = 0.9
const cLimitedRadiusKm = 0.6

// Screen-space assist: as the camera rises the whole marker is scaled up so
// the dome never falls under cMinMarkerPx, up to cMaxViewScale. Past that
// the decorations drop out and only the dome's own far-mode fleck remains,
// so the data reads when zoomed in without cluttering orbit views
const cMinMarkerPx = 5
const cMaxViewScale = 6

// Seas at or above this height (meters) are "extreme": the glyph pulses,
// mirroring how breaking-news quakes are the only animated quake markers
const cExtremeSeasMeters = 5
// Beaufort 10 (storm) in m/s: wind-only stations pulse at this and above
const cStormWindMps = 24.5
// Pressure falling this fast over 3 h (hPa) means a system is arriving
const cPressureFallingFastHpa = -1.5


// Bob-and-tilt animation, driven by the real observed sea state.
// Heave is a fraction of the model size (the model is stylized, not to
// scale, so real meters would be invisible); tilt is in radians.
const cBobMinHeaveFrac = 0.02   // Gentle sway even in calm water
const cBobMaxHeaveFrac = 0.12   // Heave at/above reference seas
const cBobMinTiltRad = 0.02
const cBobMaxTiltRad = 0.14
const cBobReferenceSeasMeters = 5  // Wave height that maps to max motion
const cBobDefaultPeriodSec = 7     // Used when the station reports no period

// Color scale for wave height (meters)
// Calm (0-1m) = blue, Moderate (1-3m) = green, Rough (3-5m) = yellow, Very Rough (5+m) = red
function getWaveHeightColor(waveHeight: number): color {
	if (waveHeight < 1) return rgba(0.2, 0.6, 1.0, 0.8)       // Calm - blue
	if (waveHeight < 2) return rgba(0.3, 0.8, 0.5, 0.8)       // Slight - teal
	if (waveHeight < 3) return rgba(0.5, 0.9, 0.3, 0.8)       // Moderate - green
	if (waveHeight < 4) return rgba(0.9, 0.9, 0.2, 0.8)       // Rough - yellow
	if (waveHeight < 5) return rgba(1.0, 0.6, 0.1, 0.8)       // Very Rough - orange
	return rgba(1.0, 0.2, 0.2, 0.8)                            // High - red
}

// Color scale for wind speed (m/s) on stations that don't report waves:
// a pale-to-amber ramp so coastal stations read as a different class from
// the blue-green-red sea-state buoys, converging on red only at gale force
function getWindSpeedColor(windMps: number): color {
	if (windMps < 3.4) return rgba(0.78, 0.82, 0.9, 0.7)      // Light air / light breeze
	if (windMps < 8) return rgba(0.88, 0.9, 0.78, 0.75)       // Gentle / moderate breeze
	if (windMps < 13.9) return rgba(0.98, 0.85, 0.4, 0.8)     // Fresh / strong breeze
	if (windMps < 20.8) return rgba(1.0, 0.6, 0.2, 0.85)      // Near gale / gale
	return rgba(1.0, 0.25, 0.2, 0.9)                           // Strong gale and above
}

// Reporting, but neither waves nor wind: a neutral marker with no false scale
const cLimitedColor = rgba(0.6, 0.72, 0.85, 0.6)
// Tide gauge (water level only): a steel blue outside both the sea-state
// and wind palettes, since it carries neither reading
const cTideColor = rgba(0.45, 0.62, 0.85, 0.8)
// Nothing reported in 30 days
const cInactiveColor = rgba(0.55, 0.6, 0.68, 0.5)
// DART tsunameters: violet so they stay distinct from the sea-state scale;
// muted and hollow while no water-column reading has arrived
const cDartColor = rgba(0.65, 0.5, 0.9, 0.6)
const cDartLiveColor = rgba(0.72, 0.56, 1.0, 0.9)
const cPulseTsunami = rgba(1.0, 0.2, 0.3, 1)

/** Compass degrees, or null for the feed's missing sentinels (-99) and junk */
function validDirection(deg: number | null | undefined): number | null {
	return deg != null && deg >= 0 && deg <= 360 ? deg : null
}

// Shared model instance - loaded once and reused by all buoy nodes
let sharedBuoyModel: NodeBase | null = null
let modelLoadPromise: Promise<void> | null = null

async function loadSharedModel(context: StovokorContext): Promise<void> {
	if (sharedBuoyModel) return
	if (modelLoadPromise) {
		await modelLoadPromise
		return
	}

	modelLoadPromise = (async () => {
		const gltf = new GLTF(context)
		const modelNode = await gltf.loadModel(cBuoyModelURL)
		if (modelNode) {
			sharedBuoyModel = new NodeBase()
			sharedBuoyModel.transform = matrix.scale(cModelScale)
			sharedBuoyModel.addNode(modelNode)
		}
	})()

	await modelLoadPromise
}

export class BuoyNode extends GestureNode implements InfoPanelProvider {
	public readonly id: string
	public readonly focusable: boolean = true
	/** Current position; drifting buoys move with each located observation */
	public coordinate: geographic
	private cameraAdapter: EntityCameraAdapter
	private hitSphere = new sphere(cHitSphereRadius)
	private modelRotation = matrix.identity

	public feature: Feature<Point, BuoyEntry>
	public entry: BuoyEntry
	/** 3D sea-state marker in the station's east/up/north frame */
	public readonly marker: BuoyMarkerNode
	private modelContainer: NodeBase
	private modelLoaded = false
	private modelVisible = false
	private modelRadius = cHitSphereRadius
	private markerRadiusKm = cLimitedRadiusKm
	private selectionSphere?: SelectionSphereNode
	private focusAnim?: animation
	private focusBlend = 0
	private bobPhase = 0

	constructor(context: EarthBrowserContext, feature: Feature<Point, BuoyEntry>) {
		const lon = feature.geometry.coordinates[0]
		const lat = feature.geometry.coordinates[1]
		const buoyEntry = feature.properties
		const buoyId = buoyEntry.station_id ?? feature.id

		super(context)
		this.id = `buoy-${buoyId}`
		this.feature = feature
		this.entry = buoyEntry

		// Per-station animation phase so neighboring markers don't breathe in lockstep
		let hash = 0
		for (let i = 0; i < this.id.length; i++) {
			hash = (hash * 31 + this.id.charCodeAt(i)) | 0
		}
		this.bobPhase = Math.abs(hash % 628) / 100	// 0..2π

		this.marker = new BuoyMarkerNode(context, this.bobPhase)
		this.coordinate = this.placeAt(lon, lat)
		this.updateMarkerColor()
		this.addNode(this.marker)

		// Create container for the 3D model with radial orientation
		this.modelContainer = new NodeBase()
		this.modelContainer.transform = matrix.axisRotation(vec3.unitX, trig.pi)
		this.addNode(this.modelContainer)

		// Start loading the shared model
		void this.loadModel()

		this.cameraAdapter = new EntityCameraAdapter(context, this, cBuoyFocusDistance, cBuoyFocusMaxZoomRadius)
	}

	override destroy(): void {
		this.focusAnim?.stop()
		super.destroy()
	}

	/**
	 * Put the node at a lon/lat (degrees): the radial model frame, the
	 * translation, and the marker's east/up/north frame expressed in this
	 * node's rotated frame to screen
	 */
	private placeAt(lonDeg: number, latDeg: number): geographic {
		const coordinate = new geographic(lonDeg * trig.rad, latDeg * trig.rad, cBuoyModelAltitudeMeters)

		const xRot = matrix.axisRotation(vec3.unitX, coordinate.latitude + trig.half_pi)
		const yRot = matrix.axisRotation(vec3.unitY, coordinate.longitude + trig.pi)
		this.modelRotation = yRot.premultiply(xRot)
		this.transform = matrix.translate(coordinate.cartesian).premultiply(this.modelRotation)

		const toLocal = this.transform.invert()
		const lonRad = coordinate.longitude
		const latRad = coordinate.latitude
		const northWorld = new vec3(
			-Math.sin(lonRad) * Math.sin(latRad),
			Math.cos(latRad),
			-Math.cos(lonRad) * Math.sin(latRad)
		)
		const eastWorld = new vec3(Math.cos(lonRad), 0, -Math.sin(lonRad))
		// Pure rotation, so the unit vectors stay unit
		this.marker.setFrame(
			toLocal.multVec3(eastWorld, 0),
			toLocal.multVec3(coordinate.normal, 0),
			toLocal.multVec3(northWorld, 0)
		)
		return coordinate
	}

	// ========== InfoPanelProvider ==========

	public getInfoPanelData(): InfoPanelData {
		const name = this.entry?.name
		const id = this.entry?.station_id ?? String(this.feature.id ?? "Buoy")
		return {
			title: name && name !== id ? name : id,
			subtitle: name && name !== id ? id : undefined,
			theme: "buoy",
			contentType: "buoy",
			contentData: {
				type: "buoy",
				buoy: this.entry
			}
		}
	}

	// ========== Focus Lifecycle ==========

	public onFocusGained(camera: Camera): void {
		this.cameraAdapter.attach(camera)

		// Push to browser history for back/forward navigation
		const buoyId = this.entry?.station_id
		if (buoyId) {
			pushTargetToHistory('buoy', buoyId)
		}

		// Experimental selection sphere disabled for now
		// this.showSelectionSphere()
		this.animateFocusBlend(1)
	}

	public onFocusLost(): void {
		this.cameraAdapter.detach()
		this.hideSelectionSphere()
		this.animateFocusBlend(0)
	}

	/** Fade the marker down to 10% while focused so it doesn't wash out the model */
	private animateFocusBlend(target: number): void {
		this.focusAnim?.stop()
		this.focusAnim = new animation()
			.from(this.focusBlend)
			.to(target)
			.duration(0.5)
			.easing(easing.quadraticInOut)
			.onUpdate(value => {
				this.focusBlend = value
				this.marker.opacity = 1 - 0.9 * value
			})
		this.focusAnim.start()
	}


	public onFocusUpdate(): void {
		this.cameraAdapter.update()
	}

	public focusTransition(camera: Camera): FocusTransitionConfig {
		return {
			target: () => this.cameraAdapter.getTargetCameraState(camera),
			params: {
				duration: 1,
				easing: "easeInOut"
			}
		}
	}

	// ========== Command Handlers ==========

	public handleCommand(command: string, data?: unknown): boolean {
		return this.cameraAdapter.handleCommand(command, data)
	}

	private async loadModel(): Promise<void> {
		await loadSharedModel(this.context)
		if (sharedBuoyModel && !this.modelLoaded) {
			this.modelLoaded = true
			this.modelContainer.addNode(sharedBuoyModel)
			// Model bounds only, not the marker's
			this.modelRadius = this.modelContainer.calculateBoundingBox().sphere.radius
		}
	}

	/**
	 * Encode what the station is reporting right now. A wave reading makes
	 * a sea-state marker: size and color from wave height, wedge into the
	 * swell, ring thickness from period, wind shaft from wind. Wind-only
	 * (coastal) stations are triangles on a separate wind scale; DART and
	 * silent stations are hollow so they can't pass for live readings.
	 * Motion is reserved for alerts: extreme seas, storm wind, or pressure
	 * falling fast, so a calm ocean stays calm on the globe.
	 */
	private updateMarkerColor(): void {
		const obs = this.entry?.latest_observation ?? null
		const category = classifyBuoy(this.entry, obs)
		const waveHeight = obs?.wave_height ?? null
		const windSpeed = obs?.wind_speed ?? null
		const tendency = obs?.pressure_tendency ?? null

		const style: BuoyMarkerStyle = {
			shape: BuoyMarkerShape.Limited,
			color: cLimitedColor,
			radiusKm: cLimitedRadiusKm,
			waveDirectionDeg: null,
			periodSec: null,
			windDirectionDeg: null,
			windSpeedMps: null,
			pulse: 0,
		}

		if (category === "dart") {
			// Hollow until the tsunameter reports a water-column height; event
			// reporting (it has detected a wave) is the one DART state that moves
			const reporting = isDartReporting(obs)
			style.shape = BuoyMarkerShape.Dart
			style.color = reporting ? cDartLiveColor : cDartColor
			style.radiusKm = cDartRadiusKm
			style.solid = reporting
			if (reporting && isDartEventMode(obs)) {
				style.color = cPulseTsunami
				style.pulse = 1
			}
		} else if (category === "inactive") {
			style.shape = BuoyMarkerShape.Silent
			style.color = cInactiveColor
			style.radiusKm = cSilentRadiusKm
		} else if (waveHeight != null) {
			style.shape = BuoyMarkerShape.SeaState
			style.color = getWaveHeightColor(waveHeight)
			style.radiusKm = cSeaStateMinRadiusKm
				+ cSeaStateRadiusPerMeterKm * Math.min(cSeaStateMaxWaveMeters, Math.max(0, waveHeight))
			// Mean wave direction, else the swell partition's direction
			style.waveDirectionDeg = validDirection(obs?.wave_direction) ?? validDirection(obs?.swell_direction)
			style.periodSec = obs?.wave_period ?? obs?.swell_period ?? null
			style.windDirectionDeg = validDirection(obs?.wind_direction)
			style.windSpeedMps = windSpeed
			if (waveHeight >= cExtremeSeasMeters) {
				style.pulse = 1
			}
		} else if (windSpeed != null) {
			style.shape = BuoyMarkerShape.Coastal
			style.color = getWindSpeedColor(windSpeed)
			style.radiusKm = cCoastalRadiusKm
			style.windDirectionDeg = validDirection(obs?.wind_direction)
			style.windSpeedMps = windSpeed
			if (windSpeed >= cStormWindMps) {
				style.pulse = 1
			}
		} else if (category === "tide") {
			style.shape = BuoyMarkerShape.Coastal
			style.color = cTideColor
			style.radiusKm = cCoastalRadiusKm
		}

		if (style.pulse === 0 && tendency != null && tendency <= cPressureFallingFastHpa
			&& (style.shape === BuoyMarkerShape.SeaState || style.shape === BuoyMarkerShape.Coastal)) {
			style.pulse = 0.8
		}

		this.markerRadiusKm = style.radiusKm
		this.marker.setStyle(style)
	}

	/**
	 * Update the buoy color based on latest observation data
	 */
	public updateColor(): void {
		this.updateMarkerColor()
	}

	/**
	 * Update feature data from BuoyEntry
	 * Accepts either a GeoJSON Feature or a flat BuoyEntry
	 */
	public updateFeature(feature: Feature<Point, BuoyEntry> | BuoyEntry): void {
		// Check if this is a flat BuoyEntry vs GeoJSON Feature
		if (!('type' in feature) || feature.type !== 'Feature') {
			// This is a flat BuoyEntry
			const buoyEntry = feature
			this.feature = {
				type: 'Feature',
				id: buoyEntry.station_id,
				geometry: {
					type: 'Point',
					coordinates: [buoyEntry.lon, buoyEntry.lat]
				},
				properties: buoyEntry
			}
			this.entry = buoyEntry
		} else {
			// GeoJSON Feature with BuoyEntry
			this.feature = feature
			this.entry = feature.properties
		}

		// Drifting buoys: follow the located observation
		const [lon, lat] = this.feature.geometry.coordinates
		if (Math.abs(lon * trig.rad - this.coordinate.longitude) > 1e-7
			|| Math.abs(lat * trig.rad - this.coordinate.latitude) > 1e-7) {
			this.coordinate = this.placeAt(lon, lat)
		}

		// Update visual representation
		this.updateColor()
	}

	/**
	 * Show the selection sphere gizmo (called on hover)
	 */
	public showSelectionSphere(): void {
		if (!this.selectionSphere) {
			const radius = this.hitSphere.radius
			this.selectionSphere = new SelectionSphereNode(this.context, {
				radius: radius,
				arrowWidth: radius * 0.08,
				arrowheadLength: radius * 0.18,
				arrowheadWidth: radius * 0.15,
				arrowOffset: radius * 0.02,
				elevateEquator: true
			})

			this.selectionSphere.transform = matrix.translate(0, 0, 0.5)
			this.addNode(this.selectionSphere)
		}
		this.selectionSphere.visible = true
	}

	/**
	 * Hide the selection sphere gizmo (called on hover out)
	 */
	public hideSelectionSphere(): void {
		if (this.selectionSphere) {
			this.selectionSphere.visible = false
		}
	}

	override render(state: RenderState): RenderActionSet[] {
		if (!this.visible) {
			return []
		}

		// Only render the 3D model when the camera is close enough to see it
		const distance = state.camera.position.subtract(this.transform.position).length
		this.modelVisible = this.modelLoaded && distance < cModelVisibleDistance
		this.modelContainer.visible = this.modelVisible

		if (this.modelVisible) {
			this.updateBobAnimation(state)
		}

		// Screen-space assist for the marker. World size of one logical pixel
		// at this depth comes from the projection's vertical scale
		// (perspective: 1/tan(fov/2), where clip w is the depth; ortho: w = 1)
		const p = state.projection.glArrayAccess()
		const depth = state.viewDepthOf(this.transform.position)
		const clipW = p[11] * -depth + p[15]
		const worldPerPixel = p[5] > 0 && state.height > 0 ? (2 * Math.abs(clipW)) / (p[5] * state.height) : 0
		const neededScale = worldPerPixel > 0 ? (cMinMarkerPx * worldPerPixel) / this.markerRadiusKm : 1
		this.marker.setViewScale(Math.min(cMaxViewScale, Math.max(1, neededScale)), neededScale <= cMaxViewScale)

		const hitRadius = Math.max(cHitSphereRadius, this.marker.extentKm, this.modelVisible ? this.modelRadius : 0)
		if (Math.abs(hitRadius - this.hitSphere.radius) > hitRadius * 0.05) {
			this.hitSphere = new sphere(hitRadius)
		}

		return super.render(state)
	}

	/**
	 * Ride the waves: heave and tilt the model with amplitude from the real
	 * observed wave height and frequency from the real dominant wave period,
	 * so a buoy in 5 m seas visibly labors while one in calm water barely sways
	 */
	private updateBobAnimation(state: RenderState): void {
		const obs = this.entry?.latest_observation
		const waveHeight = obs?.wave_height ?? 0.3	// Assume near-calm when unreported
		const wavePeriod = obs?.wave_period ?? cBobDefaultPeriodSec

		// Sea state 0..1 against the reference (extreme) wave height
		const sea = Math.min(1, Math.max(0, waveHeight / cBobReferenceSeasMeters))
		const heave = this.modelRadius * (cBobMinHeaveFrac + (cBobMaxHeaveFrac - cBobMinHeaveFrac) * sea)
		const tilt = cBobMinTiltRad + (cBobMaxTiltRad - cBobMinTiltRad) * sea

		// Seconds within the day, plus a per-station phase so neighbors don't sync
		const seconds = (state.realTime.julian % 1) * 86400 + this.bobPhase
		const omega = trig.two_pi / Math.min(16, Math.max(3, wavePeriod))

		// Two incommensurate frequencies per axis make the motion read as swell
		// rather than a metronome; heave leads the pitch slightly, like a real
		// buoy cresting before it rolls down the back of a wave
		const heaveOffset = Math.sin(seconds * omega) * heave
		const tiltX = Math.sin(seconds * omega * 0.83 + 1.3) * tilt
		const tiltZ = Math.sin(seconds * omega * 0.61) * tilt

		// Container base orientation is the π flip around X (local +Y up inside)
		this.modelContainer.transform = matrix.axisRotation(vec3.unitX, trig.pi + tiltX)
			.multiply(matrix.axisRotation(vec3.unitZ, tiltZ))
			.multiply(matrix.translate(0, heaveOffset, 0))
	}

	override gestureIntersection(state: RenderState): GestureHitResult | undefined {
		const dist = this.hitSphere.intersect(state.ray)
		if (dist > 0) {
			const hitPoint = state.ray.pointAt(dist)
			return { node: this, hitPoint: hitPoint, distance: dist }
		}
		return undefined
	}
}
